/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { connect } from 'react-redux';
import Box from '@material-ui/core/Box';
import { useHistory } from 'react-router';
import { Grid } from '@material-ui/core';
import { object, string, shape } from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import withStyles from '@material-ui/core/styles/withStyles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { getBrand } from '../../state/ducks/Common/Common-Selectors';
import fetchOrderDetailsAPI from '../../../../../apis/order-apis/fetchOrderDetails';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getAccessTokenSafely } from '../../../../../state/ducks/Member/ducks/Auth/Plugin/Auth/Auth';
import DeliveryExtraFee from '../../../Checkout/containers/CheckoutFloral/Partials/OrderTotalSummary/Partials/DeliveryExtraFee';

const styles = (theme) => ({
    CustomerWrapperPLA: {
        '&.PLA': {
            padding: '0 15px',
            maxWidth: '1270px',
            margin: '25px auto',
            flexDirection: 'column',
        },
        '& [class^="DeliveryExtraFee"]': {
            display: 'none',
        },
    },
    OrderDetail: {
        '& .header': {
            marginBottom: '0.5rem',
            '& span, a': {
                fontSize: '1.25rem !important',
                fontWeight: 600,
            },
            '& a': {
                color: 'black',
                textDecoration: 'none',
                fontSize: '16px !important',
                '&:hover': {
                    textDecoration: 'underline',
                },
            },
        },
        '& .billingDetails': {
            [theme.breakpoints.up(482)]: {
                justifyContent: 'space-around',
            },
        },
        '& img': {
            width: '150px',
        },

        '& .d-block': {
            display: 'block',
            '& span.label': {
                width: '220px',
                marginBottom: '10px',
                display: 'inline-block',
            },
        },

        '& .heading': {
            fontWeight: 600,
            display: 'block',
            marginBottom: '10px',
        },

        '& .mb-2': {
            marginBottom: '10px',
        },
        '& .detailAlign': {
            float: 'left',
            padding: '10px',
        },
    },
});

const getformattedOrderDetails = (orderData) => {
    const tempOrderDetails = {};
    orderData.orders.forEach((orderValue) => {
        const order = { ...orderValue };
        order.items[0].tax = order.billingProfile.tax;

        if (tempOrderDetails[order.orderNumber]) {
            const item = order.items[0];
            item.recipientProfiles = order.recipientProfiles[0];
            tempOrderDetails[order.orderNumber].items[0].deliveryFee = +tempOrderDetails[order.orderNumber].items[0].deliveryFee + +order.billingProfile.deliveryFee;
            tempOrderDetails[order.orderNumber].billingProfile.shippingCharge = +tempOrderDetails[order.orderNumber].billingProfile.shippingCharge + +order.billingProfile.shippingCharge;
            tempOrderDetails[order.orderNumber].items.push(item);
        } else {
            order.items[0].recipientProfiles = order.recipientProfiles[0];
            tempOrderDetails[order.orderNumber] = order;
        }
    });

    const formattedOrderDetails = JSON.parse(JSON.stringify(tempOrderDetails));

    // merge if we are getting same sku as multiple item.
    Object.keys(tempOrderDetails).forEach((orderId) => {
        formattedOrderDetails[orderId].items = [formattedOrderDetails[orderId].items[0]];

        tempOrderDetails[orderId].items.forEach((item, index) => {
            if (index !== 0 && tempOrderDetails[orderId].items[0].productSku === item.productSku) {
                formattedOrderDetails[orderId].items[0].quantity = +formattedOrderDetails[orderId].items[0].quantity + +item.quantity;
            } else if (tempOrderDetails[orderId].items[0].productSku !== item.productSku) {
                formattedOrderDetails[orderId].items.push(item);
            }
        });
    });

    return Object.values(formattedOrderDetails)[0];
};

const OrderDetailsPLA = ({
    brand,
    classes,
    userProfile,
    featureFlags,
    dataDictionary,
}) => {
    const history = useHistory();
    const { orderNumber } = useParams();
    const [order, setOrder] = useState({});
    const [orderDetails, setOrderDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const isMobile = !useMediaQuery('(min-width:600px)');

    const redirectToOrderListing = () => history.push('/account/orders');

    useEffect(() => {
        (async () => {
            const token = await getAccessTokenSafely();
            const orderResponse = await fetchOrderDetailsAPI(token, { email: userProfile.email, orderNumber, user: 30016 }, {}, featureFlags['which-ross-api-version-enabled']);

            if (!orderResponse?.data?.orders?.length) {
                redirectToOrderListing();
            }

            // assemble items in 1 order
            const orderData = orderResponse.data;
            if (orderData.orders) {
                orderData.order = getformattedOrderDetails(orderData);
            }

            // setting default values to 0
            orderData.taxTotal = 0;
            orderData.productTotal = 0;
            orderData.shippingTotal = 0;
            orderData.discountTotal = 0;
            orderData.deliveryFee = 0;

            // calculate totals for the order
            orderData.order.items.forEach((item) => {
                orderData.deliveryFee += +item.deliveryFee;

                const data = orderData?.orders.find((details) => details?.items?.[0]?.productSku === item?.productSku);
                let billingProfile = data?.billingProfile;
                if (!billingProfile) {
                    billingProfile = orderData?.orders[0]?.billingProfile;
                }

                orderData.shippingTotal = ((+orderData.shippingTotal || 0) + (+billingProfile.shippingCharge)).toFixed(2);
                orderData.discountTotal = ((+orderData.discountTotal || 0) + (Math.abs(+billingProfile.promotionDiscountAmount))).toFixed(2);

                if (billingProfile.orderSubTotal) {
                    orderData.taxTotal = ((+orderData.taxTotal || 0) + (+item.tax || 0) * (+item.quantity)).toFixed(2);
                    orderData.productTotal = ((+orderData.productTotal || 0) + (+item.price) * (+item.quantity)).toFixed(2);
                } else {
                    orderData.taxTotal = ((+orderData.taxTotal || 0) + (+billingProfile.tax)).toFixed(2);

                    if (+billingProfile.totalCharge) {
                        orderData.productTotal = (+orderData.productTotal + +(+billingProfile.totalCharge - (+billingProfile.tax + (+billingProfile.shippingCharge)) + (+orderData.discountTotal))).toFixed(2);
                    }
                }
            });

            if (orderData.deliveryFee && orderData.shippingTotal) {
                orderData.shippingTotal = (orderData.shippingTotal - orderData.deliveryFee).toFixed(2);
            }

            setOrder(orderData.order);
            setOrderDetails(orderData);
            setLoading(false);
        })();
    }, []);

    if (loading) {
        return <></>;
    }

    const address = order?.billingProfile?.addressDetail;
    const justifyContent = isMobile ? 'center' : 'space-between';
    const sourceId = orderNumber?.indexOf('W') >= 0 ? '' : order?.items[0]?.sourceId || 'W0100';
    const phoneNumber = order?.billingProfile?.phones?.phone?.number || (orderDetails?.phoneNumber || '');

    return (
        <Grid container className={`${classes.CustomerWrapperPLA} ${brand.code} ${classes.OrderDetail}`}>
            <Grid
                container
                className="header"
                columns={{ sm: 12, md: 6 }}
                justifyContent={justifyContent}
            >
                <span>Order Details: {sourceId}{orderNumber}</span>
                {!isMobile && <Link to="/account/orders">Back to My Orders</Link>}
            </Grid>

            <Box
                boxShadow={3}
                spacing={3}
                style={{ padding: 15, marginTop: '1rem' }}
            >
                <Grid
                    container
                    columns={{ sm: 12, md: 6 }}
                    className="billingDetails"
                >
                    <div>
                        <span className="heading">Billing Address:</span>
                        <span className="d-block mb-2">{order.billingProfile.firstName} {order.billingProfile.lastName}</span>
                        <span className="d-block mb-2">{order?.billingProfile?.emailAddress}</span>
                        <span className="d-block mb-2">{address.addressLine1}</span>
                        {address?.addressLine2 ? <span className="d-block mb-2">{address.addressLine2}</span> : ''}
                        {address?.addressLine3 ? <span className="d-block mb-2">{address.addressLine3}</span> : ''}
                        <span className="d-block mb-2">{address.cityName}, {address.state}, {address.zipCode}</span>
                        {phoneNumber ? <span className="d-block mb-2">{phoneNumber}</span> : ''}
                    </div>
                    <div>
                        <span className="heading">Order Payment Summary:</span>
                        <span className="d-block">
                            <span className="label">Subtotal: </span>
                            <span>${orderDetails.productTotal}</span>
                        </span>

                        <span className="d-block">
                            <span className="label">Shipping Charges / Surcharges: </span>
                            <span>${orderDetails.shippingTotal}</span>
                        </span>

                        {
                            orderDetails?.deliveryFee
                                ? (
                                    <DeliveryExtraFee
                                        orderDetail
                                        flags={featureFlags}
                                        stateDeliveryFee={{
                                            4: orderDetails.deliveryFee,
                                        }}
                                        dataDictionary={dataDictionary}
                                    />
                                ) : ''
                        }

                        <span className="d-block">
                            <span className="label">Tax: </span>
                            <span>${orderDetails.taxTotal}</span>
                        </span>

                        {
                            +orderDetails.discountTotal
                                ? (
                                    <span className="d-block">
                                        <span className="label">Discount: </span>
                                        <span>${Math.abs(orderDetails.discountTotal)}</span>
                                    </span>
                                ) : ''
                        }

                        <span className="d-block">
                            <span className="label">Total: </span>
                            <span>${orderDetails.orderTotal}</span>
                        </span>
                    </div>
                </Grid>
            </Box>

            <Box
                boxShadow={3}
                spacing={3}
                style={{ padding: 15, marginTop: '1rem' }}
            >
                <Grid
                    container
                    justifyContent="center"
                    columns={{ sm: 12, md: 6 }}
                    style={{ gap: 15 }}
                >
                    {
                        order.items.map((item) => {
                            const recipientProfiles = item.recipientProfiles;
                            const shippingAddress = recipientProfiles.addressDetail[0];
                            const shippingPhoneNumber = recipientProfiles.phones[0].phone[0].number;

                            return (
                                <div>
                                    <div className="detailAlign">
                                        <img src={item.prdImgCtg ? item.prdImgCtg.replace('http://', 'https://') : item.prdImgCtg} alt="product-image" />
                                    </div>
                                    <div className="detailAlign">
                                        <span className="heading">Gift Details</span>
                                        <span className="d-block mb-2">{item.productName}</span>
                                        <span className="d-block mb-2">Product SKU: #{item.productSku}</span>
                                        <span className="d-block mb-2">Price: ${item.price}</span>
                                        <span className="d-block mb-2">Quantity: {item.quantity}</span>
                                    </div>
                                    <div className="detailAlign">
                                        <span className="heading">Shipping Address</span>
                                        <span className="d-block mb-2">{recipientProfiles.firstName} {recipientProfiles.lastName}</span>
                                        <span className="d-block mb-2">{shippingAddress.addressLine1}</span>
                                        <span className="d-block mb-2">{shippingAddress.cityName}, {shippingAddress.state}, {shippingAddress.zipCode}</span>
                                        <span className="d-block mb-2">{shippingPhoneNumber}</span>
                                    </div>
                                </div>
                            );
                        })
                    }
                </Grid>
            </Box>
        </Grid>
    );
};

OrderDetailsPLA.propTypes = {
    brand: shape({
        code: string.isRequired,
    }).isRequired,
    classes: object.isRequired,
    userProfile: object.isRequired,
    featureFlags: object.isRequired,
    dataDictionary: object.isRequired,
};

const mapDispatchToProps = (state) => ({
    brand: getBrand(state),
    featureFlags: getFeatureFlags(state),
});

export default connect(mapDispatchToProps)(withStyles(styles)(OrderDetailsPLA));
